import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { RecruiterSkeleton, ErrorState, EmptyState } from "@/components/shared";
import { CohortIntegrity } from "@/components/CohortIntegrity";
import { EvidenceDrawer } from "@/components/EvidenceDrawer";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { KanbanSquare, Search, Columns3, List, SlidersHorizontal, ChevronDown, MoreHorizontal, FileDown, Link2, Activity, GitCompareArrows, ArrowRightLeft, Share2, X, Check } from "lucide-react";
import { BoardCard, STAGES } from "@/components/pipeline/BoardCard";
import { PipelineList } from "@/components/pipeline/PipelineList";
import { PipelineFilters } from "@/components/pipeline/PipelineFilters";
import { SharesDialog, useSharesDialog } from "@/components/pipeline/SharesDialog";
import { LayoutGroup } from "framer-motion";
import { useMotionPresets } from "@/lib/motion";
import { useLiveFlagToasts } from "@/hooks/useLiveFlagToasts";

export { nextDigestHint } from "@/components/pipeline/SharesDialog";

const TAG_FIELDS = ["skills", "seniority", "domain", "work_mode"];
const tagsOf = (c) => (c.tags ? TAG_FIELDS.flatMap((f) => c.tags[f] || []) : []);

function toCsv(rows) {
  const esc = (v) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const headers = ["Name", "Email", "Stage", "Overall", "Objective %", "Situational %", "Confidence", "Recommendation", "Advisory flags", "Integrity", "Applied"];
  const body = rows.map((c) => [c.name, c.email, c.stage, c.overall_score ?? "", c.objective_score ?? "", c.subjective_score ?? "", c.confidence ?? "", c.debrief?.recommendation ?? "", (c.violations || []).length, c.integrity?.score ?? "", c.applied_at ? new Date(c.applied_at).toLocaleDateString() : ""]);
  return [headers, ...body].map((r) => r.map(esc).join(",")).join("\n");
}

export default function Pipeline() {
  const { jobId } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(jobId || params.get("job") || null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [active, setActive] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [q, setQ] = useState("");
  const [tagFilter, setTagFilter] = useState(new Set());
  const [sort, setSort] = useState("score");
  const [view, setView] = useState(() => localStorage.getItem("hs_pipeline_view") || "board");
  const [showRejected, setShowRejected] = useState(false);
  const [saved, setSaved] = useState([]);
  const shares = useSharesDialog();
  const M = useMotionPresets();
  useLiveFlagToasts(candidates);

  const loadSaved = () => api.get("/searches/saved").then(({ data }) => setSaved(data)).catch(() => {});
  useEffect(() => { loadSaved(); }, []);
  useEffect(() => { localStorage.setItem("hs_pipeline_view", view); }, [view]);

  const loadJobs = () => {
    setLoading(true); setErr(null);
    api.get("/jobs").then((r) => {
      const published = r.data.filter((j) => j.status === "published");
      setJobs(published);
      if (published[0]) setSelectedJob((cur) => (cur && published.some((j) => j.id === cur) ? cur : published[0].id));
      else setLoading(false);
    }).catch((e) => { setErr(e.message); setLoading(false); });
  };
  useEffect(loadJobs, []);

  const loadCands = (jid) => {
    setLoading(true); setErr(null);
    api.get(`/jobs/${jid}/candidates`).then((r) => setCandidates(r.data)).catch((e) => setErr(e.message)).finally(() => setLoading(false));
  };
  useEffect(() => { if (selectedJob) { loadCands(selectedJob); setSel(new Set()); } }, [selectedJob]);

  const anyActive = candidates.some((c) => c.assessment_active);
  useEffect(() => {
    if (!anyActive || !selectedJob) return undefined;
    const t = setInterval(() => { api.get(`/jobs/${selectedJob}/candidates`).then((r) => setCandidates(r.data)).catch(() => {}); }, 15000);
    return () => clearInterval(t);
  }, [anyActive, selectedJob]);

  const toggle = (id) => {
    if (!sel.has(id) && sel.size >= 5) { toast.message("Select up to 5 candidates at once"); return; }
    setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const move = async (cand, stage) => {
    setCandidates((cs) => cs.map((c) => (c.id === cand.id ? { ...c, stage } : c)));
    try {
      const { data } = await api.patch(`/candidates/${cand.id}/stage`, { stage });
      setCandidates((cs) => cs.map((c) => (c.id === cand.id ? { ...c, stage: data.stage, stage_age: data.stage_age } : c)));
      toast.success(`${cand.name} → ${STAGES.find((s) => s.key === stage)?.label}`);
    } catch { toast.error("Could not move candidate"); loadCands(selectedJob); }
  };
  const moveSelected = async (stage) => { for (const c of candidates.filter((x) => sel.has(x.id))) await move(c, stage); setSel(new Set()); };
  const shareShortlist = async () => {
    try {
      const { data } = await api.post("/shares/shortlist", { candidate_ids: [...sel] });
      const url = `${window.location.origin}${data.path}`;
      try { await navigator.clipboard.writeText(url); toast.success(`Shortlist link copied — ${sel.size} candidates, valid 30 days`); } catch { toast.message("Shortlist link ready", { description: url }); }
      setSel(new Set());
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create shortlist link"); }
  };
  const saveSearch = async () => {
    try { await api.post("/searches/saved", { job_id: selectedJob, term: q.trim() }); toast.success(`Saved “${q.trim()}” — we'll flag new matches`); loadSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save search"); }
  };
  const applySaved = (s) => { setQ(s.term); if (selectedJob !== s.job_id) setSelectedJob(s.job_id); api.post(`/searches/saved/${s.id}/seen`).then(loadSaved).catch(() => {}); };
  const delSaved = (id) => api.delete(`/searches/saved/${id}`).then(loadSaved).catch(() => toast.error("Could not remove"));

  const allTags = useMemo(() => [...new Set(candidates.flatMap(tagsOf))].slice(0, 24), [candidates]);
  const visible = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return candidates
      .filter((c) => !ql || [c.name, c.parsed_profile?.headline, ...(c.cv_terms || []), ...tagsOf(c)].filter(Boolean).join(" ").toLowerCase().includes(ql))
      .filter((c) => tagFilter.size === 0 || tagsOf(c).some((t) => tagFilter.has(t)))
      .sort((a, b) => (sort === "waiting"
        ? (a.stage_age?.entered_at ? Date.parse(a.stage_age.entered_at) : Infinity) - (b.stage_age?.entered_at ? Date.parse(b.stage_age.entered_at) : Infinity)
        : (b.overall_score ?? -1) - (a.overall_score ?? -1)));
  }, [candidates, q, tagFilter, sort]);

  const exportCsv = () => {
    if (!visible.length) { toast.message("No candidates to export"); return; }
    const title = jobs.find((j) => j.id === selectedJob)?.title || "pipeline";
    const url = URL.createObjectURL(new Blob([`\ufeff${toCsv(visible)}`], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a"); a.href = url; a.download = `${title.replace(/\s+/g, "-").toLowerCase()}-candidates.csv`; a.click(); URL.revokeObjectURL(url);
    toast.success(`Exported ${visible.length} candidate${visible.length === 1 ? "" : "s"} to CSV`);
  };

  const job = jobs.find((j) => j.id === selectedJob);
  const filterCount = (q.trim() ? 1 : 0) + tagFilter.size + (sort !== "score" ? 1 : 0);
  const rejected = visible.filter((c) => c.stage === "rejected");
  const boardStages = STAGES.filter((s) => s.key !== "rejected" || showRejected);

  return (
    <div className={cn("recruiter-page space-y-6", sel.size > 0 && "pipeline-selection-active")} data-testid="pipeline-page">
      <header className="flex flex-wrap items-start gap-3" data-testid="pipeline-header">
        <div className="flex-1 min-w-[240px]">
          <DropdownMenu>
            <DropdownMenuTrigger asChild disabled={!jobs.length}>
              <button className="flex items-center gap-2 text-left max-w-full group" data-testid="pipeline-job-select">
                <h1 className="text-2xl sm:text-[28px] font-display font-semibold tracking-tight truncate" data-testid="pipeline-title">{job?.title || "Candidates"}</h1>
                {jobs.length > 1 && <ChevronDown className="h-5 w-5 text-muted-foreground group-hover:text-foreground shrink-0" />}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72 max-h-80 overflow-y-auto">
              {jobs.map((j) => <DropdownMenuItem key={j.id} onClick={() => setSelectedJob(j.id)} data-testid={`pipeline-option-${j.id}`}><span className="flex-1 truncate">{j.title}</span>{j.id === selectedJob && <Check className="h-4 w-4 text-primary" />}</DropdownMenuItem>)}
            </DropdownMenuContent>
          </DropdownMenu>
          <p className="text-sm text-muted-foreground mt-1">
            {candidates.length} candidate{candidates.length === 1 ? "" : "s"} · ranked by {sort === "waiting" ? "time waiting" : "fit"} · <button onClick={() => nav("/recruiter/jobs")} className="text-primary hover:underline" data-testid="pipeline-all-jobs">All jobs</button>
            {anyActive && <span className="ml-2 inline-flex items-center gap-1 text-primary" data-testid="live-monitoring"><Activity className="h-3.5 w-3.5 motion-safe:animate-pulse" /> {candidates.filter((c) => c.assessment_active).length} testing now</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex p-1 rounded-lg border border-border bg-card" role="radiogroup" aria-label="View">
            {[["board", "Board", Columns3], ["list", "List", List]].map(([k, l, Icon]) => <button key={k} role="radio" aria-checked={view === k} onClick={() => setView(k)} data-testid={`pipeline-view-${k}`} className={cn("flex items-center gap-1.5 px-3 h-8 rounded-md text-sm transition-colors", view === k ? "bg-foreground/[0.08] font-semibold" : "text-muted-foreground hover:text-foreground")}><Icon className="h-4 w-4" />{l}</button>)}
          </div>
          <Popover>
            <PopoverTrigger asChild><Button variant="outline" className="gap-2 h-10 font-semibold" data-testid="pipeline-filters-btn"><SlidersHorizontal className="h-4 w-4" /> Filters{filterCount ? ` · ${filterCount}` : ""}</Button></PopoverTrigger>
            <PopoverContent align="end" className="w-[340px]">
              <PipelineFilters q={q} setQ={setQ} onSave={saveSearch} canSave={!!q.trim() && !!selectedJob} saved={saved.filter((s) => s.job_id === selectedJob)} onApplySaved={applySaved} onDelSaved={delSaved}
                tags={allTags} tagFilter={tagFilter} toggleTag={(t) => setTagFilter((p) => { const n = new Set(p); n.has(t) ? n.delete(t) : n.add(t); return n; })} clearTags={() => setTagFilter(new Set())} sort={sort} setSort={setSort} />
            </PopoverContent>
          </Popover>
          {selectedJob && <CohortIntegrity key={selectedJob} jobId={selectedJob} compact />}
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="outline" size="icon" className="h-10 w-10" aria-label="More actions" data-testid="pipeline-more"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={exportCsv} disabled={!visible.length} data-testid="pipeline-export-csv"><FileDown className="h-4 w-4 mr-2" /> Export CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={shares.show} data-testid="open-shared-links"><Link2 className="h-4 w-4 mr-2" /> Shared links</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {loading ? <RecruiterSkeleton variant="pipeline" label="Loading candidates…" />
        : err ? <ErrorState message={err} onRetry={() => (selectedJob ? loadCands(selectedJob) : loadJobs())} />
        : jobs.length === 0 ? <EmptyState icon={KanbanSquare} title="No live roles yet" description="Publish a role and its applicants appear here, ranked by explainable fit." action={<Button onClick={() => nav("/recruiter/jobs")} className="mt-2" data-testid="pipeline-create-role">View roles</Button>} />
        : candidates.length === 0 ? <EmptyState icon={KanbanSquare} title="No candidates yet" description="Share the role's public link — applicants appear here as soon as they apply and finish the assessment." action={<Button variant="outline" className="mt-2" onClick={() => nav(`/recruiter/jobs/${selectedJob}/publish`)} data-testid="pipeline-share-role">Share this role</Button>} />
        : visible.length === 0 ? <EmptyState icon={Search} title="No matching candidates" description="Try another name or skill, or clear your filters to see everyone in this role." action={<Button variant="outline" onClick={() => { setQ(""); setTagFilter(new Set()); }} data-testid="pipeline-clear-filters">Clear filters</Button>} />
        : view === "list" ? <PipelineList rows={visible} selected={sel} onToggle={toggle} onOpen={setActive} onMove={move} />
        : (
          <div className="space-y-3">
            <LayoutGroup>
            <div className="flex gap-4 overflow-x-auto pb-3 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x" data-testid="pipeline-board">
              {boardStages.map((stage) => {
                const inStage = visible.filter((c) => c.stage === stage.key);
                return (
                  <section key={stage.key} className="hs-card p-3 flex flex-col gap-3 min-w-[260px] flex-1 snap-start min-h-[420px]" data-testid={`column-${stage.key}`} aria-label={stage.label}>
                    <header className="flex items-center gap-2 px-1 pt-1"><span className="text-[15px] font-semibold">{stage.label}</span><span className="text-xs px-1.5 py-0.5 rounded-md bg-foreground/[0.07] text-muted-foreground" data-testid={`stage-count-${stage.key}`}>{inStage.length}</span></header>
                    {inStage.map((c) => <BoardCard key={c.id} c={c} selected={sel.has(c.id)} onToggle={toggle} onOpen={setActive} onMove={move} reduce={M.reduce} />)}
                    {!inStage.length && <p className="text-xs text-muted-foreground px-1">No one here yet</p>}
                  </section>
                );
              })}
            </div>
            </LayoutGroup>
            {rejected.length > 0 && <button onClick={() => setShowRejected((v) => !v)} className="text-sm text-muted-foreground hover:text-foreground" data-testid="toggle-rejected">{showRejected ? "Hide" : "Show"} not progressing · {rejected.length}</button>}
          </div>
        )}

      <EvidenceDrawer candidate={active} onClose={() => setActive(null)} onUpdated={(u) => { setCandidates((cs) => cs.map((c) => (c.id === u.id ? u : c))); setActive(u); }} />

      {sel.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex flex-wrap items-center gap-2 w-[calc(100%-2rem)] sm:w-max rounded-2xl border border-border bg-card/95 backdrop-blur-xl px-4 py-3 shadow-2xl animate-in slide-in-from-bottom-2" data-testid="compare-bar">
          <span className="text-sm font-semibold pr-2 sm:border-r border-border" data-testid="compare-selected-count">{sel.size} selected</span>
          <Button size="sm" onClick={() => nav(`/recruiter/compare/${selectedJob}?ids=${[...sel].join(",")}`)} disabled={sel.size < 2} className="gap-1.5" data-testid="pipeline-compare-go"><GitCompareArrows className="h-4 w-4" /> Compare</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button size="sm" variant="secondary" className="gap-1.5" data-testid="bulk-move"><ArrowRightLeft className="h-4 w-4" /> Move to…</Button></DropdownMenuTrigger>
            <DropdownMenuContent align="center">{STAGES.map((s) => <DropdownMenuItem key={s.key} onClick={() => moveSelected(s.key)} data-testid={`bulk-move-${s.key}`}>{s.label}</DropdownMenuItem>)}</DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" variant="secondary" onClick={shareShortlist} disabled={sel.size < 2} className="gap-1.5" data-testid="pipeline-share-shortlist"><Share2 className="h-4 w-4" /> Share shortlist</Button>
          <button onClick={() => setSel(new Set())} aria-label="Clear selection" data-testid="compare-clear" className="p-1.5 rounded-md text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
      )}
      <SharesDialog state={shares} />
    </div>
  );
}
